import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Form, Dropdown, Badge } from 'react-bootstrap';
import { Star, Check, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { GlowpickProduct, GlowpickPaginationInfo } from '../../../../models/Glowpick';
import { GlowpickPagination } from './GlowpickPagination';
import { IdBadge, IdBadgeType } from '../../../../components/ui/badge/IdBadge';
import { ROUTES } from '../../../../routes/routes';

export type NavigationTab = 'newest' | 'awards' | 'brands' | 'category' | 'search';
export type RankingType = 'brand' | 'category';
export type AwardPeriod = '2025-1H' | '2024-2H' | '2024-1H' | '2023-2H' | '2023-1H' | '2022-2H' | '2022-1H';
export type SortOption = 'newest' | 'reviews' | 'rating' | 'price-high' | 'price-low' | 'ranking';

interface GlowpickProductsTableProps {
  loading: boolean;
  glowpickProducts: GlowpickProduct[];
  selectedGlowpickProduct: GlowpickProduct | null;
  onProductClick: (product: GlowpickProduct) => void;
  onShowIngredients: (product: GlowpickProduct) => void;
  onShowDescription: (product: GlowpickProduct) => void;
  // Navigation props
  activeTab: NavigationTab;
  brands: string[];
  categories: string[];
  subcategories: string[];
  awardCategories: string[];
  selectedCategory?: string;
  selectedSubcategory?: string;
  selectedBrand?: string;
  selectedRankingType?: RankingType;
  selectedAwardPeriod?: AwardPeriod;
  sortBy: SortOption;
  queryLimit?: number;
  lastUpdatedInfo?: { productId: string; date: string } | null;
  paginationInfo?: GlowpickPaginationInfo;
  onTabChange: (tab: NavigationTab) => void;
  onCategoryChange?: (category: string) => void;
  onSubcategoryChange?: (subcategory: string) => void;
  onRankingTypeChange?: (type: RankingType) => void;
  onAwardPeriodChange?: (period: AwardPeriod) => void;
  onSortChange: (sortBy: SortOption) => void;
  onQueryLimitChange?: (limit: number) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  // Search props
  searchProductId?: string;
  onSearchProductIdChange?: (id: string) => void;
  onSearchById?: () => void;
}

export const GlowpickProductsTable: React.FC<GlowpickProductsTableProps> = ({
  loading,
  glowpickProducts,
  selectedGlowpickProduct,
  onProductClick,
  onShowIngredients,
  onShowDescription,
  activeTab,
  brands,
  categories,
  subcategories,
  selectedCategory = '',
  selectedSubcategory = '',
  selectedBrand = '',
  selectedAwardPeriod,
  sortBy,
  queryLimit,
  lastUpdatedInfo,
  paginationInfo,
  onTabChange,
  onCategoryChange,
  onSubcategoryChange,
  onAwardPeriodChange,
  onSortChange,
  onQueryLimitChange,
  onPageChange,
  onPageSizeChange,
  searchProductId,
  onSearchProductIdChange,
  onSearchById,
}) => {
  const navigate = useNavigate();
  const [hoveredProductId, setHoveredProductId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [focusedProductIndex, setFocusedProductIndex] = useState<number>(-1);
  const [hideMatched, setHideMatched] = useState<boolean>(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Helper function to check product status
  const getProductStatus = React.useCallback((product: GlowpickProduct) => {
    const hasMatches = (product.metadata?.cpProducts?.length ?? 0) > 0;
    const isNotFound = product.metadata?.['notFoundInCoupang'] === true;

    if (hasMatches) {
      return 'matched';
    }
    if (isNotFound) {
      return 'not-found';
    }
    return 'unmatched';
  }, []);

  // Glowpick-inspired styles
  const glowpickStyles = {
    textGray: { color: '#333333' },
    textRed: { color: '#eb3d48' },
    textMuted: { color: '#666666' },
  };

  // Simple sort options without grouping
  const sortOptions = React.useMemo(() => {
    const baseOptions = [
      { value: 'newest', label: 'Newest' },
      { value: 'reviews', label: 'Most Reviews' },
      { value: 'rating', label: 'Rating' },
      { value: 'price-high', label: 'Price High' },
      { value: 'price-low', label: 'Price Low' },
    ];

    // Add context-specific ranking option based on active tab
    if (activeTab === 'brands') {
      baseOptions.push({ value: 'ranking', label: 'Brand Ranking' });
    } else if (activeTab === 'awards') {
      baseOptions.push({ value: 'ranking', label: 'Award Ranking' });
    } else if (activeTab === 'category') {
      baseOptions.push({ value: 'ranking', label: 'Category Ranking' });
    } else {
      // New Arrival tab - show general ranking
      baseOptions.push({ value: 'ranking', label: 'Best Ranked' });
    }

    return baseOptions;
  }, [activeTab]);

  const filteredProducts = React.useMemo(() => {
    let filtered = glowpickProducts;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(query) ||
          product.brand.toLowerCase().includes(query) ||
          product.category?.toLowerCase().includes(query) ||
          product.subcategory?.toLowerCase().includes(query),
      );
    }

    // Apply hide matched filter
    if (hideMatched) {
      filtered = filtered.filter((product) => {
        const status = getProductStatus(product);
        return status !== 'matched';
      });
    }

    return filtered;
  }, [glowpickProducts, searchQuery, hideMatched, getProductStatus]);

  // Server already handles category/subcategory filtering, so we just use filteredProducts directly

  // Server handles category/subcategory filtering, so we keep the simple subcategories list

  // Server handles category/subcategory navigation, no complex client-side logic needed

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!tableContainerRef.current) {
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedProductIndex((prev) => {
            const newIndex = prev < filteredProducts.length - 1 ? prev + 1 : prev;
            return newIndex;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedProductIndex((prev) => {
            const newIndex = prev > 0 ? prev - 1 : prev;
            return newIndex;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedProductIndex >= 0 && focusedProductIndex < filteredProducts.length) {
            const focusedProduct = filteredProducts[focusedProductIndex];
            if (focusedProduct) {
              onProductClick(focusedProduct);
            }
          }
          break;
        case 'Escape':
          setFocusedProductIndex(-1);
          break;
      }
    };

    // Add event listener to the table container
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
      // Make container focusable
      container.setAttribute('tabindex', '0');
    }

    return () => {
      if (container) {
        container.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [focusedProductIndex, filteredProducts, onProductClick]);

  // Auto-scroll focused product into view
  useEffect(() => {
    if (focusedProductIndex >= 0 && tableContainerRef.current) {
      const productRows = tableContainerRef.current.querySelectorAll('tbody tr');
      const focusedRow = productRows[focusedProductIndex] as HTMLElement;
      if (focusedRow) {
        focusedRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [focusedProductIndex]);

  // Reset focus when products change
  useEffect(() => {
    setFocusedProductIndex(-1);
  }, [filteredProducts]);

  return (
    <div className="d-flex flex-column h-100">
      {/* Header with Tabs and Controls */}
      <div className="d-flex flex-column">
        {/* Banner Section */}
        <div className="d-flex align-items-center p-2 border-bottom" style={{ backgroundColor: '#f8f9fa' }}>
          <div className="d-flex align-items-center">
            <div
              className="me-2 d-flex align-items-center justify-content-center rounded"
              style={{
                width: '40px',
                height: '40px',
                backgroundColor: '#eb3d48',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '18px',
              }}
            >
              G
            </div>
            <div>
              <p className="mb-0 small text-muted">세상 모든 뷰티의 시작과 끝</p>
              <div className="d-flex align-items-center">
                <p className="mb-0 fw-bold" style={glowpickStyles.textRed}>
                  GLOWPICK
                </p>
                {selectedGlowpickProduct?.scAgentEngData?.screeningResult?.flag === true && (
                  <span
                    className="ms-2"
                    title={
                      selectedGlowpickProduct.scAgentEngData.screeningResult.flagReason ||
                      'This product has been flagged'
                    }
                    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                  >
                    <AlertTriangle size={18} style={{ color: '#ffc107' }} />
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="ms-auto">
            <div className="text-end">
              {lastUpdatedInfo && (
                <div>
                  <div>
                    <small className="text-muted">Last updated:</small>
                  </div>
                  <div>
                    <small className="text-muted">{lastUpdatedInfo.date}</small>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="d-flex border-bottom">
          <button
            type="button"
            className="btn btn-link text-decoration-none py-2 px-3"
            style={{
              color: activeTab === 'newest' ? glowpickStyles.textRed.color : '#6c757d',
              backgroundColor: 'transparent',
              borderBottom:
                activeTab === 'newest' ? `3px solid ${glowpickStyles.textRed.color}` : '3px solid transparent',
            }}
            onClick={(e) => {
              e.preventDefault();
              onTabChange('newest');
            }}
          >
            <h6 className="mb-0">New Arrival</h6>
          </button>
          <button
            type="button"
            className="btn btn-link text-decoration-none py-2 px-3"
            style={{
              color: activeTab === 'awards' ? glowpickStyles.textRed.color : '#6c757d',
              backgroundColor: 'transparent',
              borderBottom:
                activeTab === 'awards' ? `3px solid ${glowpickStyles.textRed.color}` : '3px solid transparent',
            }}
            onClick={(e) => {
              e.preventDefault();
              onTabChange('awards');
            }}
          >
            <h6 className="mb-0">Awards</h6>
          </button>
          <button
            type="button"
            className="btn btn-link text-decoration-none py-2 px-3"
            style={{
              color: activeTab === 'brands' ? glowpickStyles.textRed.color : '#6c757d',
              backgroundColor: 'transparent',
              borderBottom:
                activeTab === 'brands' ? `3px solid ${glowpickStyles.textRed.color}` : '3px solid transparent',
            }}
            onClick={(e) => {
              e.preventDefault();
              onTabChange('brands');
            }}
          >
            <h6 className="mb-0">Brands</h6>
          </button>
          <button
            type="button"
            className="btn btn-link text-decoration-none py-2 px-3"
            style={{
              color: activeTab === 'category' ? glowpickStyles.textRed.color : '#6c757d',
              backgroundColor: 'transparent',
              borderBottom:
                activeTab === 'category' ? `3px solid ${glowpickStyles.textRed.color}` : '3px solid transparent',
            }}
            onClick={(e) => {
              e.preventDefault();
              onTabChange('category');
            }}
          >
            <h6 className="mb-0">Category</h6>
          </button>
          <button
            type="button"
            className="btn btn-link text-decoration-none py-2 px-3"
            style={{
              color: activeTab === 'search' ? glowpickStyles.textRed.color : '#6c757d',
              backgroundColor: 'transparent',
              borderBottom:
                activeTab === 'search' ? `3px solid ${glowpickStyles.textRed.color}` : '3px solid transparent',
            }}
            onClick={(e) => {
              e.preventDefault();
              onTabChange('search');
            }}
          >
            <h6 className="mb-0">Search</h6>
          </button>

          {/* Settings Dropdown */}
          <div className="ms-auto d-flex align-items-center">
            <Dropdown show={showSettings} onToggle={setShowSettings}>
              <Dropdown.Toggle
                as="button"
                type="button"
                className="btn btn-link text-decoration-none py-2 px-3"
                style={{
                  color: '#6c757d',
                  backgroundColor: 'transparent',
                  border: 'none',
                }}
                onClick={(e) => {
                  e.preventDefault();
                  setShowSettings(!showSettings);
                }}
              >
                <span className="text-muted">Settings</span>
              </Dropdown.Toggle>

              <Dropdown.Menu align="end" style={{ minWidth: '260px' }}>
                <div className="px-3 py-2">
                  <h6 className="mb-3">Settings</h6>

                  {/* Query Limit */}
                  <div className="mb-3">
                    <label className="form-label small text-muted">Query Limit</label>
                    <Form.Select
                      size="sm"
                      value={queryLimit}
                      onChange={(e) => onQueryLimitChange?.(parseInt(e.target.value))}
                      className="form-select"
                    >
                      <option value={20}>20 products</option>
                      <option value={50}>50 products</option>
                      <option value={100}>100 products</option>
                      <option value={200}>200 products</option>
                      <option value={300}>300 products</option>
                    </Form.Select>
                  </div>

                  {/* Hide Discontinued */}
                  <div className="mb-2">
                    <style>
                      {`
                        .custom-red-checkbox .form-check-input:checked {
                          background-color: #eb3d48;
                          border-color: #eb3d48;
                        }
                        .custom-red-checkbox .form-check-input:focus {
                          border-color: #eb3d48;
                          box-shadow: 0 0 0 0.25rem rgba(235, 61, 72, 0.25);
                        }
                      `}
                    </style>
                    <Form.Check
                      type="checkbox"
                      id="settings-hide-matched"
                      label="Hide Already Matched Products"
                      checked={hideMatched}
                      onChange={() => setHideMatched(!hideMatched)}
                      className="custom-red-checkbox"
                    />
                  </div>
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {/* Tab Content - RESTORE ALL MISSING SECTIONS */}
        {activeTab === 'awards' && (
          <>
            <div className="d-flex align-items-center gap-2 p-2 overflow-auto" style={{ backgroundColor: 'white' }}>
              <button
                type="button"
                className={`btn btn-sm rounded-pill px-3 ${selectedAwardPeriod === '2025-1H' ? 'btn-danger' : 'btn-outline-secondary'}`}
                onClick={(e) => {
                  e.preventDefault();
                  onAwardPeriodChange?.('2025-1H');
                }}
                style={{ whiteSpace: 'nowrap' }}
              >
                2025 1H Awards
              </button>
              <button
                type="button"
                className={`btn btn-sm rounded-pill px-3 ${selectedAwardPeriod === '2024-2H' ? 'btn-danger' : 'btn-outline-secondary'}`}
                onClick={(e) => {
                  e.preventDefault();
                  onAwardPeriodChange?.('2024-2H');
                }}
                style={{ whiteSpace: 'nowrap' }}
              >
                2024 2H Awards
              </button>
              <button
                type="button"
                className={`btn btn-sm rounded-pill px-3 ${selectedAwardPeriod === '2024-1H' ? 'btn-danger' : 'btn-outline-secondary'}`}
                onClick={(e) => {
                  e.preventDefault();
                  onAwardPeriodChange?.('2024-1H');
                }}
                style={{ whiteSpace: 'nowrap' }}
              >
                2024 1H Awards
              </button>
              <button
                type="button"
                className={`btn btn-sm rounded-pill px-3 ${selectedAwardPeriod === '2023-2H' ? 'btn-danger' : 'btn-outline-secondary'}`}
                onClick={(e) => {
                  e.preventDefault();
                  onAwardPeriodChange?.('2023-2H');
                }}
                style={{ whiteSpace: 'nowrap' }}
              >
                2023 2H Awards
              </button>
            </div>
          </>
        )}

        {activeTab === 'brands' && (
          <div
            className="d-flex align-items-center gap-2 p-2 overflow-auto"
            style={{
              backgroundColor: 'white',
              maxHeight: '120px',
              overflowY: 'auto',
              borderBottom: '1px solid #e9ecef',
            }}
          >
            {/* Show all available brands as buttons */}
            {brands.map((brand) => (
              <button
                key={brand}
                type="button"
                className={`btn btn-sm rounded-pill px-3 ${selectedBrand === brand ? 'btn-danger' : 'btn-outline-secondary'}`}
                onClick={(e) => {
                  e.preventDefault();
                  onCategoryChange?.(brand);
                }}
                style={{ whiteSpace: 'nowrap' }}
              >
                {brand}
              </button>
            ))}
          </div>
        )}

        {activeTab === 'category' && (
          <div className="d-flex flex-column">
            {/* Current category selection with clear options */}
            {selectedCategory && (
              <div
                className="d-flex align-items-center gap-2 p-2 overflow-auto"
                style={{ backgroundColor: 'white', borderBottom: '1px solid #e9ecef' }}
              >
                <div className="d-flex align-items-center gap-2 flex-nowrap">
                  <button
                    type="button"
                    className="btn btn-sm rounded-pill px-3 btn-danger d-flex align-items-center"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {selectedCategory}
                    <span
                      className="ms-2"
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onCategoryChange?.('');
                        onSubcategoryChange?.('');
                      }}
                    >
                      ×
                    </span>
                  </button>

                  {selectedSubcategory && (
                    <>
                      <span className="text-muted">›</span>
                      <button
                        type="button"
                        className="btn btn-sm rounded-pill px-3 btn-danger d-flex align-items-center"
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {selectedSubcategory}
                        <span
                          className="ms-2"
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onSubcategoryChange?.('');
                          }}
                        >
                          ×
                        </span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Category selection when none selected */}
            {!selectedCategory && (
              <div className="d-flex align-items-center gap-2 p-2 overflow-auto" style={{ backgroundColor: 'white' }}>
                {categories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    className="btn btn-sm rounded-pill px-3 btn-outline-secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      onCategoryChange?.(category);
                    }}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {category}
                  </button>
                ))}
              </div>
            )}

            {/* Subcategory selection when category is selected but no subcategory */}
            {selectedCategory && !selectedSubcategory && subcategories.length > 0 && (
              <div
                className="d-flex align-items-center gap-2 px-2 py-2 overflow-auto"
                style={{ backgroundColor: '#f8f9fa' }}
              >
                {subcategories.map((subcategory) => (
                  <button
                    key={subcategory}
                    type="button"
                    className="btn btn-sm rounded-pill px-3 btn-outline-secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      onSubcategoryChange?.(subcategory);
                    }}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {subcategory}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Search Tab Content */}
        {activeTab === 'search' && (
          <div className="d-flex align-items-center gap-2 p-3 border-bottom" style={{ backgroundColor: 'white' }}>
            <div className="d-flex align-items-center gap-2 flex-grow-1">
              <label className="text-muted mb-0" style={{ whiteSpace: 'nowrap' }}>
                Product ID:
              </label>
              <Form.Control
                type="text"
                placeholder="12345"
                value={searchProductId || ''}
                onChange={(e) => onSearchProductIdChange?.(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onSearchById?.();
                  }
                }}
                style={{ maxWidth: '150px' }}
              />
              <Button variant="danger" size="sm" onClick={onSearchById} disabled={!searchProductId?.trim()}>
                Search
              </Button>
            </div>
          </div>
        )}

        {/* Filter Controls and Product Count */}
        <div className="d-flex align-items-center justify-content-between p-2 border-bottom">
          <div className="d-flex align-items-center">
            <Form.Select
              size="sm"
              value={sortBy}
              onChange={(e) => onSortChange(e.target.value as SortOption)}
              className="me-3"
              style={{ width: 'auto' }}
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control
              type="text"
              size="sm"
              placeholder="Filter products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: '250px' }}
              className="me-3"
            />
          </div>
          <div className="d-flex align-items-center">
            <small className="text-muted">
              {filteredProducts.length} displayed out of {paginationInfo?.totalProducts || 0}
            </small>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="flex-grow-1" style={{ overflowY: 'auto', overflowX: 'hidden' }} ref={tableContainerRef}>
        {loading ? (
          <div className="d-flex align-items-center justify-content-center h-100">
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading products...</p>
            </div>
          </div>
        ) : activeTab === 'category' && !selectedCategory ? (
          <div className="d-flex align-items-center justify-content-center h-100">
            <div className="text-center text-muted">
              <div className="mb-3" style={{ fontSize: '3rem' }}>
                📂
              </div>
              <h5>Select a Category</h5>
              <p>Choose a category above to view products</p>
            </div>
          </div>
        ) : activeTab === 'brands' && !selectedBrand ? (
          <div className="d-flex align-items-center justify-content-center h-100">
            <div className="text-center text-muted">
              <div className="mb-3" style={{ fontSize: '3rem' }}>
                🏷️
              </div>
              <h5>Select a Brand</h5>
              <p>Choose a brand above to view products</p>
            </div>
          </div>
        ) : (
          <Table className="mb-0 glowpick-table">
            <style>
              {`
              .glowpick-table {
                border-spacing: 0 8px !important;
                border-collapse: separate !important;
                table-layout: fixed !important;
                width: 100% !important;
              }
              .glowpick-row {
                border-radius: 8px !important;
                overflow: hidden !important;
              }
              .glowpick-row td {
                border: none !important;
                word-wrap: break-word !important;
                overflow-wrap: break-word !important;
                max-width: 0 !important;
              }
              .glowpick-row-hovered {
                background-color: rgba(0, 0, 0, 0.05) !important;
                transform: scale(1.01) !important;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
              }
              .glowpick-row-selected {
                outline: 3px solid rgba(235, 61, 72, 0.8) !important;
                outline-offset: -3px !important;
              }
              .glowpick-row-focused {
                background-color: rgba(172, 83, 89, 0.2) !important;
              }
            `}
            </style>
            <tbody>
              {filteredProducts.map((product, index) => {
                const isSelected = selectedGlowpickProduct?.productId === product.productId;
                const isHovered = hoveredProductId === product.productId;
                const isFocused = focusedProductIndex === index;

                return (
                  <tr
                    key={product.productId}
                    className={`glowpick-row ${isSelected ? 'glowpick-row-selected' : ''} ${isHovered ? 'glowpick-row-hovered' : ''} ${isFocused ? 'glowpick-row-focused' : ''}`}
                    style={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                    }}
                    onMouseEnter={() => {
                      setHoveredProductId(product.productId);
                    }}
                    onMouseLeave={() => {
                      setHoveredProductId(null);
                    }}
                    onClick={() => {
                      setFocusedProductIndex(index);
                      onProductClick(product);
                    }}
                  >
                    {/* Row Number */}
                    <td style={{ width: '32px', minWidth: '32px', textAlign: 'center', verticalAlign: 'middle' }}>
                      {isSelected ? (
                        <div
                          style={{
                            background: 'rgba(224, 49, 84, 0.9)',
                            color: 'white',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto',
                            border: '1px solid white',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                          }}
                        >
                          <Check size={12} />
                        </div>
                      ) : (
                        <small className="text-muted" style={{ fontSize: '0.56rem', fontWeight: '400' }}>
                          {index + 1}
                        </small>
                      )}
                    </td>
                    <td style={{ width: '22%', maxWidth: '400px', minWidth: '120px' }}>
                      <div className="d-flex align-items-center justify-content-center position-relative group">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="rounded"
                          style={{
                            width: 'min(260px, 95%)',
                            height: 'min(260px, 95%)',
                            minWidth: '120px',
                            minHeight: '120px',
                            maxWidth: '100%',
                            objectFit: 'cover',
                          }}
                        />

                        {/* Glowpick Overlay */}
                        <a
                          href={product.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center text-decoration-none"
                          style={{
                            background: 'rgba(0,0,0,0.45)',
                            color: 'white',
                            fontSize: '0.7rem',
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            borderRadius: '0.3rem',
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '1';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '0';
                          }}
                        >
                          Go to Glowpick ↗
                        </a>
                      </div>
                    </td>
                    <td style={{ width: '78%' }}>
                      <div className="d-flex flex-column h-100">
                        {/* Product Summary */}
                        <div className="product-summary">
                          {/* Brand, Category and Product ID */}
                          <div className="d-flex align-items-center justify-content-between mb-1 flex-wrap gap-1">
                            <div className="d-flex align-items-center flex-wrap gap-1">
                              <button
                                type="button"
                                className="btn btn-link p-0 text-decoration-none fw-semibold"
                                style={{ ...glowpickStyles.textGray, color: '#2d3436', fontSize: '0.9rem' }}
                              >
                                {product.brand}
                              </button>
                              {product.subcategory && (
                                <span className="ms-2" style={{ fontSize: '0.8rem', color: '#666666' }}>
                                  {product.category} › {product.subcategory}
                                </span>
                              )}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <IdBadge type={IdBadgeType.GPI} value={product.productId} size="sm" />
                              {(() => {
                                const status = getProductStatus(product);
                                if (status === 'matched') {
                                  const matches = product.metadata?.['cpProducts'] || [];

                                  // For single match, link to matched detail page
                                  if (matches.length === 1 && matches[0]) {
                                    const [cpProductId, itemId] = matches[0].cpUniqueId.split('-');
                                    return (
                                      <Badge
                                        bg="success"
                                        className="px-2 py-1"
                                        style={{
                                          fontSize: '0.65rem',
                                          cursor: 'pointer',
                                          textDecoration: 'none',
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(ROUTES.sourcing.matchedDetail(cpProductId, itemId));
                                        }}
                                      >
                                        Matched ({matches.length})
                                      </Badge>
                                    );
                                  } else if (matches.length > 1) {
                                    // For multiple matches, link to matched page filtered by GPI
                                    return (
                                      <Badge
                                        bg="success"
                                        className="px-2 py-1"
                                        style={{
                                          fontSize: '0.65rem',
                                          cursor: 'pointer',
                                          textDecoration: 'none',
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(ROUTES.sourcing.matchedGpi(product.productId));
                                        }}
                                        title={`${matches.length} matches - click to view all`}
                                      >
                                        Matched ({matches.length})
                                      </Badge>
                                    );
                                  } else {
                                    // Fallback for matches without valid data
                                    return (
                                      <Badge bg="success" className="px-2 py-1" style={{ fontSize: '0.65rem' }}>
                                        Matched ({matches.length})
                                      </Badge>
                                    );
                                  }
                                } else if (status === 'not-found') {
                                  return (
                                    <Badge bg="warning" className="px-2 py-1" style={{ fontSize: '0.65rem' }}>
                                      Not Found
                                    </Badge>
                                  );
                                } else {
                                  return (
                                    <Badge bg="secondary" className="px-2 py-1" style={{ fontSize: '0.65rem' }}>
                                      Unmatched
                                    </Badge>
                                  );
                                }
                              })()}

                              {/* SKUs from ssProducts metadata */}
                              {product.metadata?.ssProducts && product.metadata.ssProducts.length > 0 && (
                                <>
                                  {product.metadata.ssProducts.map((sku, index) => (
                                    <IdBadge key={index} type={IdBadgeType.SKU} value={sku} size="sm" />
                                  ))}
                                </>
                              )}
                            </div>
                          </div>

                          {/* Product Name */}
                          <h1
                            className="fw-bold mb-1 d-flex align-items-center justify-content-between"
                            style={{
                              fontSize: '0.95rem',
                              lineHeight: '1.2',
                              wordBreak: 'break-word',
                            }}
                          >
                            <div className="d-flex align-items-center gap-2">
                              <span>{product.name}</span>
                              {product.scAgentEngData?.screeningResult?.flag === true && (
                                <span
                                  title={
                                    product.scAgentEngData.screeningResult.flagReason || 'This product has been flagged'
                                  }
                                  style={{
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    backgroundColor: 'red',
                                    padding: '2px',
                                    borderRadius: '4px',
                                  }}
                                >
                                  <AlertTriangle size={24} />
                                </span>
                              )}
                            </div>
                            <div className="d-flex gap-1">
                              <Button
                                variant="link"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onShowIngredients(product);
                                }}
                                title="View Ingredients"
                                className="text-nowrap py-1 text-muted"
                                style={{ fontSize: '0.75rem' }}
                              >
                                Ingredients
                              </Button>
                              <Button
                                variant="link"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onShowDescription(product);
                                }}
                                title="View Description"
                                className="text-nowrap py-1 text-muted"
                                style={{ fontSize: '0.75rem' }}
                              >
                                Description
                              </Button>
                            </div>
                          </h1>

                          {/* Offer Section */}
                          <div className="mb-1 text-muted d-flex align-items-center justify-content-between">
                            <span style={{ fontSize: '0.85rem' }}>
                              {product.size || 'N/A'} / {product.price?.toLocaleString() || 'N/A'}KRW
                            </span>
                            {product.awards && product.awards.length > 0 && (
                              <span
                                style={{ color: '#ffd700', fontSize: '0.7rem' }}
                                className="ms-1 d-flex align-items-center text-nowrap px-2"
                              >
                                🏆
                                <span className="ms-1 text-warning" style={{ fontSize: '0.8rem' }}>
                                  {product.awards[0]?.title?.replace(' 글로우픽 어워드', ' ') || ''}
                                  {product.awards[0]?.category?.replace(' 부문', ' ') || ''}
                                  {product.awards[0]?.position?.replace(' 위', ' ') || 'Rookie'}
                                  {product.awards.length > 1 && ` +${product.awards.length - 1}`}
                                </span>
                              </span>
                            )}
                          </div>
                          <div className="mb-2 d-flex align-items-center justify-content-between gap-2 flex-wrap">
                            <div className="d-inline-flex align-items-center flex-wrap gap-1">
                              {/* Rating & Reviews */}
                              {(product.rating !== undefined && product.rating !== null) ||
                              (product.reviews !== undefined && product.reviews !== null) ? (
                                <>
                                  <span className="me-1">
                                    {[...Array(5)].map((_, i) => (
                                      <Star
                                        key={i}
                                        size={11}
                                        className={`${i < Math.floor(product.rating || 0) ? 'text-danger' : 'text-muted'}`}
                                        fill={product.rating && product.rating > 0 ? 'currentColor' : 'none'}
                                        style={{ marginRight: '1px' }}
                                      />
                                    ))}
                                  </span>
                                  <span className="me-2" style={{ fontSize: '0.85rem', ...glowpickStyles.textGray }}>
                                    {product.rating || ''}
                                  </span>
                                  <span
                                    className="text-end me-2"
                                    style={{ fontSize: '0.8rem', ...glowpickStyles.textGray }}
                                  >
                                    Reviews {product.reviews?.toLocaleString() || 0}
                                  </span>
                                </>
                              ) : null}
                            </div>
                            {/* Rankings */}
                            {((product.rankings?.brand && product.rankings.brand.ranking > 0) ||
                              (product.rankings?.category && product.rankings.category.ranking > 0)) && (
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                {product.rankings?.category && product.rankings.category.ranking > 0 && (
                                  <span
                                    className="py-1 text-muted d-flex align-items-center"
                                    style={{ fontSize: '0.85rem' }}
                                  >
                                    <span style={{ marginRight: '2px' }}>🏅</span>
                                    {product.rankings.category.title || product.category}{' '}
                                    <span className="text-danger ms-1">{product.rankings.category.ranking}위</span>
                                  </span>
                                )}
                                {product.rankings?.brand && product.rankings.brand.ranking > 0 && (
                                  <span
                                    className="px-2 py-1 text-muted d-flex align-items-center"
                                    style={{ fontSize: '0.85rem' }}
                                  >
                                    <span style={{ marginRight: '2px' }}>🏅</span>
                                    {product.rankings.brand.title || product.brand}{' '}
                                    <span className="text-danger ms-1">{product.rankings.brand.ranking}위</span>
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      {paginationInfo && (
        <GlowpickPagination
          paginationInfo={paginationInfo}
          activeTab={activeTab}
          selectedCategory={selectedCategory}
          selectedSubcategory={selectedSubcategory}
          selectedBrand={selectedBrand}
          selectedAwardPeriod={selectedAwardPeriod || ''}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  );
};
